import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { localStorageService } from '../services/localStorageService/localStorageService';
import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import {
  shouldReconnectSupportSocket,
  supportSocketUrl,
} from './supportSocketUrl';

/** Longest gap between reconnect attempts, so a long outage doesn't hammer. */
const MAX_RECONNECT_INTERVAL_MS = 15_000;

interface SupportSocketFrame {
  type?: string;
  kind?: 'message' | 'ticket';
  ticketId?: string;
}

/**
 * Live updates for the support queue and the open conversation.
 *
 * The frame says only that a ticket changed; the data still comes from the
 * REST endpoints, which is what makes this safe to broadcast to every agent
 * and what keeps one source of truth for the transcript. Invalidating rather
 * than merging also means a missed frame — during a reconnect, say — costs
 * nothing: the next event, poll or focus repairs the view.
 *
 * Shared by URL, so mounting it on both the queue and the ticket page opens
 * one socket rather than two.
 */
export function useSupportEvents(): { connected: boolean } {
  const queryClient = useQueryClient();
  const token = localStorageService().getAccessToken();
  const url = supportSocketUrl(import.meta.env.VITE_APP_BASE_URL, token);

  const { lastMessage, readyState } = useWebSocket(url, {
    share: true,
    shouldReconnect: shouldReconnectSupportSocket,
    reconnectAttempts: Number.MAX_SAFE_INTEGER,
    reconnectInterval: (attempt: number) =>
      Math.min(1000 * 2 ** attempt, MAX_RECONNECT_INTERVAL_MS),
  });

  useEffect(() => {
    if (!lastMessage?.data) return;

    let frame: SupportSocketFrame;
    try {
      frame = JSON.parse(lastMessage.data as string);
    } catch {
      return;
    }
    // The greeting confirms the handshake; there is nothing to refetch for it.
    if (!frame || frame.type === 'ready') return;

    queryClient.invalidateQueries({
      queryKey: [DashboardQueryKey.SUPPORT_TICKETS],
    });
    queryClient.invalidateQueries({
      queryKey: [DashboardQueryKey.SUPPORT_TICKET],
    });
  }, [lastMessage, queryClient]);

  return { connected: readyState === ReadyState.OPEN };
}
